/**
 * Deposit Auto-Manager for Shopify
 * Works: Broadcast theme + Basic plan + Subscription apps
 * Includes proper cart refresh
 */

(function () {
  'use strict';

  // ========== CONFIG ==========
  const VARIANT_MAP = window.__DEPOSIT_VARIANTS__ || {};
  const DEPOSIT_VARIANT_IDS = new Set(Object.values(VARIANT_MAP).map(v => Number(v)));
  
  let isProcessing = false;

  // ========== UTILS ==========
  
  function log(...args) {
    if (window.__DEPOSIT_DEBUG__) console.log('[DEPOSIT]', ...args);
  }

  function amountToVariantId(amount) {
    const rounded = Math.round(Number(amount || 0));
    return Number(VARIANT_MAP[rounded] || 0);
  }

  function isDepositVariant(variantId) {
    return DEPOSIT_VARIANT_IDS.has(Number(variantId));
  }

  function debounce(fn, delay) {
    let timer;
    return function() {
      clearTimeout(timer);
      timer = setTimeout(fn, delay);
    };
  }

  // ========== CART API ==========

  async function getCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  }

  async function addToCart(variantId, quantity, properties) {
    const body = new URLSearchParams();
    body.set('id', String(variantId));
    body.set('quantity', String(quantity));
    
    Object.keys(properties || {}).forEach(key => {
      body.set(`properties[${key}]`, String(properties[key]));
    });

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      credentials: 'same-origin',
      body: body.toString()
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Add failed: ${res.status} ${text}`);
    }
    
    return res.json();
  }

  async function updateLine(lineIndex, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line: lineIndex, quantity })
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Update failed: ${res.status} ${text}`);
    }

    return res.json();
  }

  // Force Broadcast cart/drawer to refresh
  async function forceCartRefresh() {
    log('Forcing cart UI refresh...');
    
    // Method 1: Trigger Broadcast events
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    
    // Method 2: If on cart page, reload sections
    if (window.location.pathname === '/cart') {
      try {
        const sections = await fetch(window.location.href + '?sections=cart-items,cart-footer,cart-drawer')
          .then(r => r.json());
        
        // Update cart items container if exists
        const cartItemsContainer = document.querySelector('[data-items-holder]');
        if (cartItemsContainer && sections['cart-items']) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sections['cart-items'], 'text/html');
          const newContent = doc.querySelector('[data-items-holder]');
          if (newContent) {
            cartItemsContainer.innerHTML = newContent.innerHTML;
          }
        }
        
        // Update cart footer (totals)
        const cartFooter = document.querySelector('[data-cart-price-holder]');
        if (cartFooter && sections['cart-footer']) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sections['cart-footer'], 'text/html');
          const newFooter = doc.querySelector('[data-cart-price-holder]');
          if (newFooter) {
            cartFooter.innerHTML = newFooter.innerHTML;
          }
        }
      } catch (e) {
        log('Sections reload failed, doing full reload', e);
        window.location.reload();
      }
    } else {
      // If drawer is open, try to refresh it
      const drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.classList.contains('is-open')) {
        // Trigger drawer internal refresh
        const refreshEvent = new CustomEvent('cart:refresh');
        drawer.dispatchEvent(refreshEvent);
      }
    }
  }

  // ========== DEPOSIT SYNC LOGIC ==========

  function findMainProducts(cart) {
    const results = [];
    
    cart.items.forEach((item, idx) => {
      if (isDepositVariant(item.variant_id)) return;
      
      const props = item.properties || {};
      const depositAmount = Number(props._deposit_amount || 0);
      
      if (depositAmount <= 0) return;
      
      const mainProductId = String(props._main_product_id || item.product_id);
      
      results.push({
        productId: mainProductId,
        quantity: Number(item.quantity),
        depositAmount: depositAmount,
        title: item.product_title || item.title,
        lineIndex: idx + 1
      });
    });
    
    return results;
  }

  function findDepositLine(cart, mainProductId) {
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      
      if (!isDepositVariant(item.variant_id)) continue;
      
      const props = item.properties || {};
      const linkedId = String(props._deposit_for_product_id || '');
      
      if (linkedId === String(mainProductId)) {
        return {
          item: item,
          lineIndex: i + 1,
          quantity: Number(item.quantity)
        };
      }
    }
    
    return null;
  }

  async function syncDeposits() {
    if (isProcessing) {
      log('Already syncing, skipping...');
      return;
    }

    isProcessing = true;
    log('Starting sync...');

    let needsRefresh = false;

    try {
      const cart = await getCart();
      const mainProducts = findMainProducts(cart);
      
      log('Found main products:', mainProducts.length);

      // ADD or UPDATE deposits for each main product
      for (const main of mainProducts) {
        const depositVariantId = amountToVariantId(main.depositAmount);
        
        if (!depositVariantId) {
          log(`No variant for amount ${main.depositAmount}`);
          continue;
        }

        const existingDeposit = findDepositLine(cart, main.productId);

        if (!existingDeposit) {
          // ADD new deposit
          log(`Adding deposit for product ${main.productId}, qty=${main.quantity}`);
          
          await addToCart(depositVariantId, main.quantity, {
            '_deposit_for_product_id': main.productId,
            'Security deposit – For': main.title
          });
          
          needsRefresh = true;
          
          // Refresh cart state after add
          const updatedCart = await getCart();
          Object.assign(cart, updatedCart);
          
        } else if (existingDeposit.quantity !== main.quantity) {
          // UPDATE deposit quantity
          log(`Updating deposit qty: ${existingDeposit.quantity} → ${main.quantity}`);
          
          await updateLine(existingDeposit.lineIndex, main.quantity);
          needsRefresh = true;
        }
      }

      // REMOVE orphaned deposits (main product removed)
      const mainProductIds = new Set(mainProducts.map(m => m.productId));
      
      const finalCart = await getCart();
      
      for (let i = 0; i < finalCart.items.length; i++) {
        const item = finalCart.items[i];
        
        if (!isDepositVariant(item.variant_id)) continue;
        
        const props = item.properties || {};
        const linkedId = String(props._deposit_for_product_id || '');
        
        if (linkedId && !mainProductIds.has(linkedId)) {
          log(`Removing orphaned deposit for product ${linkedId}`);
          await updateLine(i + 1, 0);
          needsRefresh = true;
        }
      }

      log('Sync complete ✓');

      // Force UI refresh if we made changes
      if (needsRefresh) {
        await forceCartRefresh();
      }

    } catch (error) {
      console.error('[DEPOSIT ERROR]', error);
    } finally {
      isProcessing = false;
    }
  }

  // ========== FETCH INTERCEPTOR ==========

  function setupInterceptor() {
    const originalFetch = window.fetch;
    
    window.fetch = function(input, init) {
      const url = typeof input === 'string' ? input : (input?.url || '');
      
      const isCartCall = 
        url.includes('/cart/add') || 
        url.includes('/cart/change') || 
        url.includes('/cart/update');

      if (!isCartCall) {
        return originalFetch.apply(this, arguments);
      }

      // Detect if this request is adding a deposit (avoid loops)
      let variantId = 0;
      try {
        const body = init?.body;
        if (body instanceof FormData) {
          variantId = Number(body.get('id') || 0);
        } else if (typeof body === 'string') {
          variantId = Number(new URLSearchParams(body).get('id') || 0);
        } else if (body && typeof body === 'object' && body.id) {
          variantId = Number(body.id || 0);
        }
      } catch (e) {}

      const isOurDepositCall = isDepositVariant(variantId);

      return originalFetch.apply(this, arguments).then(response => {
        if (response.ok && !isOurDepositCall) {
          log('Cart updated, scheduling sync');
          scheduleSync();
        }
        return response;
      });
    };
  }

  // ========== INIT ==========

  const scheduleSync = debounce(() => {
    syncDeposits().catch(err => console.error('[DEPOSIT]', err));
  }, 400);

  function init() {
    log('Initializing...');
    
    setupInterceptor();
    
    // Run initial sync (for cart page loads)
    setTimeout(scheduleSync, 1000);
    
    log('Ready ✓');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();